import {sha256} from './worker/receipt-validation';
import { useAuth } from './context/AuthContext';

// Use environment variable for production, fall back to /api for local development.
// Accept either full ".../api" or just an origin (we'll append "/api").
// When VITE_USE_PROD_API=true in dev mode, always use proxy (/api) to reach production.
const useProdApi = import.meta.env.DEV && import.meta.env.VITE_USE_PROD_API === 'true';
const rawApiUrl = useProdApi ? undefined : (import.meta.env.VITE_API_URL as string | undefined);
const normalizedApiUrl = rawApiUrl ? rawApiUrl.replace(/\/$/, '') : undefined;
const API_BASE =
    normalizedApiUrl
        ? (normalizedApiUrl.endsWith('/api') ? normalizedApiUrl : `${normalizedApiUrl}/api`)
        : '/api';

interface ApiResponse<T = unknown> {
    success: boolean;
    data?: T;
    error?: string;
    message?: string;
    status?: number;
}

async function request<T>(
    endpoint: string,
    options: RequestInit = {},
    token?: string | null
): Promise<ApiResponse<T>> {
    const headers: HeadersInit = {
        ...options.headers,
    };

    if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    if (options.body && !(options.body instanceof FormData)) {
        (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    const res = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
    });

    return { ...await res.json() as ApiResponse<T>, status:res.status };
}

export function useApi() {
    const { token } = useAuth();

    return {
        // Expenses
        getExpenses: (params?: Record<string, string>) => {
            const query = params ? `?${new URLSearchParams(params)}` : '';
            return request(`/expenses${query}`, {}, token);
        },
        getExpense: (id: string) => request(`/expenses/${id}`, {}, token),
        createExpense: (data: Record<string, unknown>, key?: string) =>
            request('/expenses', { method: 'POST', body: JSON.stringify(data),
                headers: key ? { 'Idempotency-Key': key } : undefined }, token),
        updateExpense: (id: string, data: Record<string, unknown>) =>
            request(`/expenses/${id}`, { method: 'PUT', body: JSON.stringify(data) }, token),
        deleteExpense: (id: string) =>
            request(`/expenses/${id}`, { method: 'DELETE' }, token),

        // Documents
        uploadDocument: (expenseId: string, file: File, type: string, isPrimary = false) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('document_type', type);
            formData.append('is_primary', String(isPrimary));
            return request(`/documents/expense/${expenseId}`, { method: 'POST', body: formData }, token);
        },
        getDocument: async (id: string) => {
            const response = await fetch(`${API_BASE}/documents/${id}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!response.ok) throw new Error('Unable to download original document');
            return response.blob();
        },
        deleteDocument: (id: string) =>
            request(`/documents/${id}`, { method: 'DELETE' }, token),
        getExpenseDocuments: (expenseId: string) =>
            request(`/documents/expense/${expenseId}`, {}, token),
        setPrimaryDocument: (id: string) =>
            request(`/documents/${id}/primary`, { method: 'PUT' }, token),

        // OCR
        getOcrPolicy: () => request('/ocr/policy', {}, token),
        extractFromReceipt: async (file: File, localText?: string) => {
            const formData = new FormData();
            formData.append('file', file);
            if (localText !== undefined) {
                formData.append('local_text', localText);
                formData.append('processor', 'local-v1');
                formData.append('original_sha256', await sha256(await file.arrayBuffer()));
            }
            return request('/ocr/extract', { method: 'POST', body: formData }, token);
        },
        extractFromText: (text: string) =>
            request('/ocr/extract-text', { method: 'POST', body: JSON.stringify({ text }) }, token),
        getAvailableModels: () => request('/ocr/models', {}, token),
        testAllModels: (file: File) => {
            const formData = new FormData();
            formData.append('file', file);
            return request('/ocr/test-models', { method: 'POST', body: formData }, token);
        },

        // Eligibility
        checkEligibility: (item: string) =>
            request('/ocr/eligibility', { method: 'POST', body: JSON.stringify({ item }) }, token),

        // Duplicates
        checkDuplicates: (data: { date_of_service: string; amount: number; provider?: string }) =>
            request('/duplicates/check', { method: 'POST', body: JSON.stringify(data) }, token),
        mergeDuplicate: (targetId: string, newData: Record<string, unknown>) =>
            request('/duplicates/merge', {
                method: 'POST',
                body: JSON.stringify({ target_expense_id: targetId, new_data: newData }),
            }, token),

        // Compliance
        getComplianceDashboard: () => request('/compliance/dashboard', {}, token),

        beginOperation: (data: Record<string,unknown>) => request('/operations', {method:'POST',body:JSON.stringify(data)},token),
        getPendingOperations: (kind:string,expenseId?:string) => request(`/operations?${new URLSearchParams({kind,...(expenseId ? {expense_id:expenseId}:{})})}`,{},token),
        completeOperation: (key:string) => request(`/operations/${key}/complete`,{method:'POST'},token),
        abandonOperation: (key:string) => request(`/operations/${key}/abandon`,{method:'POST'},token),
        getPaymentContext: () => request('/payments/context',{},token),
        getPatientPayments: () => request('/payments',{},token),
        createPatientPayment: (data:Record<string,unknown>) => request('/payments',{method:'POST',body:JSON.stringify(data)},token),
        reviewPatientPayment: (id:string,data:Record<string,unknown>) => request(`/payments/${id}/review`,{method:'PUT',body:JSON.stringify(data)},token),
        saveAmountFacts: (id:string,data:Record<string,unknown>) => request(`/payments/expense/${id}/amounts`,{method:'PUT',body:JSON.stringify(data)},token),
        getExpenseEvidence: (id:string) => request(`/payments/expense/${id}/evidence`,{},token),
        linkExpenseEvidence: (id:string,data:Record<string,unknown>) => request(`/payments/expense/${id}/evidence`,{method:'POST',body:JSON.stringify(data)},token),
        // Financial review and immutable receipt ledger.
        getReceiptLedger: (id: string) => request(`/ledger/expense/${id}`, {}, token),
        saveReceiptReview: (id: string, data: Record<string, unknown>) =>
            request(`/ledger/expense/${id}/review`, { method:'PUT', body:JSON.stringify(data) }, token),
        saveHsaDate: (data: Record<string, unknown>) =>
            request('/ledger/hsa', { method:'PUT', body:JSON.stringify(data) }, token),
        addLedgerEvent: (id: string, data: Record<string, unknown>, key: string) =>
            request(`/ledger/expense/${id}/events`, { method:'POST',body:JSON.stringify(data),headers:{'Idempotency-Key':key} }, token),
        // Export originals/metadata with Authorization, never token-bearing URLs.
        downloadExport: async (format: 'csv' | 'json' | 'zip', params: Record<string,string>) => {
            const response = await fetch(`${API_BASE}/export/${format}?${new URLSearchParams(params)}`, {
                headers: token ? {Authorization:`Bearer ${token}`} : {},
            });
            if (!response.ok) throw new Error('Export failed; originals may be missing or corrupted.');
            return response.blob();
        },
        getExportSummary: (params?: Record<string, string>) => {
            const query = params ? `?${new URLSearchParams(params)}` : '';
            return request(`/export/summary${query}`, {}, token);
        },
    };
}
